import React from 'react';
import { Helmet } from 'react-helmet';

const codes = [
  {
    code: 'CO-1',
    title: 'Deductible amount not met.',
    fix: 'Patient must meet their deductible before insurance pays. Bill patient or apply towards deductible.'
  },
  {
    code: 'CO-2',
    title: 'Coinsurance amount.',
    fix: 'Coinsurance is the patient responsibility. Bill the patient for their share.'
  },
  {
    code: 'CO-4',
    title: 'Procedure code is inconsistent with the modifier used.',
    fix: 'Review CPT/HCPCS modifier rules and update the modifier accordingly.'
  },
  {
    code: 'CO-11',
    title: 'Diagnosis inconsistent with procedure.',
    fix: 'Ensure the diagnosis code justifies the procedure. Update diagnosis if necessary.'
  },
  {
    code: 'CO-15',
    title: 'Authorization or precertification not obtained.',
    fix: 'Obtain retro authorization if allowed, or educate staff to secure pre-approval before services.'
  },
  {
    code: 'CO-16',
    title: 'Claim/service lacks information or has submission/billing errors.',
    fix: 'Review the claim for missing or incorrect details. Resubmit with required info.'
  },
  {
    code: 'CO-18',
    title: 'Duplicate claim/service.',
    fix: 'Claim was already submitted. Only submit once unless correction is needed.'
  },
  {
    code: 'CO-22',
    title: 'Claim exceeds the benefit maximum for this time period or occurrence.',
    fix: 'Check patient plan limits. Bill patient or seek alternate coverage if applicable.'
  },
  {
    code: 'CO-23',
    title: 'Services not covered because this is a health plan exclusion.',
    fix: 'Confirm service coverage. If excluded, bill the patient directly.'
  },
  {
    code: 'CO-27',
    title: 'Patient insurance coverage has expired.',
    fix: 'Verify and update insurance details. Contact patient for active coverage.'
  },
  {
    code: 'CO-29',
    title: 'Claim submitted past the timely filing limit.',
    fix: 'Check payer policy. Appeal if documentation supports timely submission.'
  },
  {
    code: 'CO-45',
    title: 'Charge exceeds fee schedule or maximum allowable amount.',
    fix: 'Adjust to payer allowable amount. Write off non-covered portion.'
  },
  {
    code: 'CO-50',
    title: 'Service not medically necessary per payer policy.',
    fix: 'Submit medical records for appeal or obtain ABN before service if possible.'
  },
  {
    code: 'CO-97',
    title: 'Payment adjusted because procedure is included in another service.',
    fix: 'Check for code bundling. Modify claim to unbundle if appropriate.'
  },
];

const DenialCodes = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "MedicalWebPage",
    "name": "Common Medical Billing Denial Codes Guide",
    "description": "Comprehensive guide to CO denial codes in medical billing with solutions to resolve claim denials and improve revenue cycle management",
    "publisher": {
      "@type": "Organization",
      "name": "MDeRCM"
    }
  };

  return (
    <>
      <Helmet>
        <title>Common Medical Billing Denial Codes (CO) | Healthcare Claims Denial Guide | MDeRCM</title>
        <meta 
          name="description" 
          content="Complete guide to CO denial codes in medical billing. Learn how to resolve common claim denials, reduce rejections, and improve revenue cycle management with expert solutions." 
        />
        <meta 
          name="keywords" 
          content="medical billing denial codes, CO denial codes, claim denial reasons, healthcare denial management, revenue cycle management denial codes, medical billing rejections" 
        />
        <link rel="canonical" href="https://mdercm.com/resources/denial-codes" />
        
        <meta property="og:title" content="Medical Billing Denial Codes Guide | MDeRCM" />
        <meta property="og:description" content="Expert guide to understanding and resolving CO denial codes in medical billing and revenue cycle management." />
        <meta property="og:url" content="https://mdercm.com/resources/denial-codes" />
        <meta property="og:type" content="article" />
        
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      </Helmet>

      <div style={{ fontFamily: "'Poppins', sans-serif", padding: '60px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <h1 style={{ fontSize: '34px', fontWeight: '700', marginBottom: '10px' }}>
          Common CO Denial Codes in Medical Billing
        </h1>
        <p style={{ fontSize: '16px', color: '#555', marginBottom: '40px', maxWidth: '800px' }}>
          Below are the most frequently encountered CO (Contractual Obligation) denial codes in medical billing. 
          Understand the reasons behind each code and how to address them for successful claim resolution and improved revenue cycle management.
        </p>

        {codes.map((item, index) => (
          <div key={index} style={{
            border: '1px solid #E1ECF3',
            backgroundColor: '#F8FCFF',
            padding: '25px 30px',
            borderRadius: '8px',
            marginBottom: '20px',
            boxShadow: '0 2px 6px rgba(0,0,0,0.04)'
          }}>
            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#F26522', marginBottom: '10px' }}>{item.code}</h2>
            <p style={{ fontSize: '16px', fontWeight: '500', color: '#222', marginBottom: '6px' }}>{item.title}</p>
            <p style={{ fontSize: '15px', color: '#666' }}><strong>Fix:</strong> {item.fix}</p>
          </div>
        ))}

        {/* Additional SEO Content */}
        <div style={{ 
          marginTop: '60px', 
          padding: '40px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '12px' 
        }}>
          <h3 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '20px', color: '#001F2B' }}>
            How to Reduce Medical Billing Denials
          </h3>
          <p style={{ fontSize: '16px', lineHeight: 1.8, color: '#333', marginBottom: '20px' }}>
            Understanding denial codes is crucial for effective revenue cycle management. Our healthcare revenue cycle management services 
            help reduce denial rates by 40-60% through automated verification, coding accuracy checks, and proactive denial prevention strategies.
          </p>
          <ul style={{ fontSize: '16px', lineHeight: 2, color: '#333', paddingLeft: '20px' }}>
            <li>Implement automated insurance verification before services</li>
            <li>Use AI-powered coding accuracy checks</li>
            <li>Maintain updated payer contract knowledge</li>
            <li>Train staff on common denial codes and prevention</li>
            <li>Track denial patterns and address root causes</li>
          </ul>
          
          <div style={{ marginTop: '30px', textAlign: 'center' }}>
            <a 
              href="/free-trial"
              style={{
                display: 'inline-block',
                padding: '15px 40px',
                backgroundColor: '#F26522',
                color: '#fff',
                borderRadius: '30px',
                fontSize: '16px',
                fontWeight: 600,
                textDecoration: 'none',
                transition: 'all 0.3s ease'
              }}
            >
              Reduce Your Denials Today
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default DenialCodes;